/**
 * AdminDanismanCockpit.jsx
 *
 * Danışman Paneli — Faz 5 sadeleştirmesi (bkz. proje-yoneticisi görev
 * talimatı, 2026-08-13).
 *
 * Route: /admin/cockpit
 * Erişim: admin, superadmin, danisman
 *
 * Sayfa dört içerik bloğundan oluşur:
 *   1) Ödemesi Gelenler özeti (tıklanınca OdemeModal açılır)
 *   2) Satış Taleplerim (yalnızca danışman, varsayılan kapalı) — bkz.
 *      Faz 6 (proje-yoneticisi görev talimatı, 2026-08-13 — Atanan Leadler
 *      kaldırma turu): eskiden AdminAssignedLeads.jsx'te (/danisman/leads)
 *      yaşayan panel BİREBİR aynı mantıkla buraya taşındı, sayfa kaldırıldı.
 *   3) Hatırlatmalar (Eskiyen 50) — tablo (# / Ad Soyad / Açıklama / Ertele)
 *   4) Datalar — Potansiyeller / Son Aramalar / Yaklaşan Hatırlatmalar /
 *      Data Detay Geçmiş sekmeleri (bkz. components/crm/DatalarBlock.jsx).
 *      Eskiden ayrı bir sayfaydı (`/danisman/datalar`, AdminDanismanDatalar.jsx)
 *      — o sayfa kaldırıldı, eski adres bu sayfaya redirect edilir (App.js).
 *
 * Lead detay modalındaki "İşlemler" bölümü (Dönüştür / Satışa Hazır
 * İşaretle / Kimlik Oluştur / Profil) — bkz. DanismanCockpitShared.jsx
 * LeadDetayModal dosya başı yorumu.
 *
 * Mola (ara ver/dön) özelliği KORUNDU.
 *
 * Backend prefix: /api/danisman/cockpit
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import ErrorBoundary, { BlockErrorFallback } from '../components/ErrorBoundary';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtMoney } from '../utils/money';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import {
  useLeadStatusOptions,
  OdemeModal,
  HatirlatmalarPanel,
  SatisTalepleriPanel,
  LeadDetayModal,
  ErrorRetryCrm,
} from '../components/crm/DanismanCockpitShared';
import DatalarBlock from '../components/crm/DatalarBlock';
import { Coffee, CreditCard, AlarmClock, CalendarClock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDanismanCockpit() {
  const { lang } = useLanguage();
  const { user, authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const { options: statusOptions } = useLeadStatusOptions();

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const location = useLocation();

  // Satış Taleplerim paneli — yalnızca danışman rolünde anlamlı (bkz.
  // SatisTalepleriPanel dosya başı yorumu). LeadDetayModal içindeki "Talep
  // Gönderildi" linki `/admin/cockpit#satis-taleplerim-panel` ile buraya
  // deep-link yapar — panel varsayılan KAPALI ama bu hash ile açılıp
  // görünüme kaydırılır (bkz. DatalarBlock'taki ?tab=...&lead_id= deseniyle
  // AYNI ruh, farklı mekanizma — burada sekme değil katlanabilir panel var).
  const salesReqPanelRef = useRef(null);
  const salesReqDeepLink = location.hash === '#satis-taleplerim-panel';
  useEffect(() => {
    if (salesReqDeepLink) {
      document.getElementById('satis-taleplerim-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Summary (Ödemesi Gelenler özeti + mola durumu)
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);

  // Ödemesi gelenler modal — Faz D: `pencere` sekmesi (bkz.
  // routes/danisman_cockpit.py::cockpit_odemesi_gelenler). 'varsayilan' = eski
  // davranış (gecikmiş+bugün, pencere param'sız çağrı), 'yaklasan' = önümüzdeki
  // 7 gün (?pencere=yaklasan).
  const [odemeItems, setOdemeItems] = useState([]);
  const [odemeModal, setOdemeModal] = useState(false);
  const [odemeLoading, setOdemeLoading] = useState(false);
  const [odemeTab, setOdemeTab] = useState('varsayilan');

  // Mola
  const [molaLoading, setMolaLoading] = useState(false);
  const [molaBaslangic, setMolaBaslangic] = useState(null);
  const [molaElapsed, setMolaElapsed] = useState(0);

  // Lead görüşme detayı — TEK giriş noktası (Faz 4: LeadDetayModal, ortada Dialog).
  const [gorusmeLead, setGorusmeLead] = useState(null);
  const openLeadDetay = (lead) => setGorusmeLead(lead);

  // Kaydet sonrası Hatırlatmalar panelini tazelemek için — panel artık
  // forwardRef ile `refetch()` metodu dışa veriyor (bkz. DanismanCockpitShared
  // içindeki useImperativeHandle). Eskiden `key` değiştirip tam remount
  // yapılıyordu; bu, kullanıcının bağlamını (kaydırma pozisyonu, açık
  // dropdown) gereksiz yere sıfırlıyordu.
  const remindersRef = useRef(null);
  // Datalar bloğu (DatalarBlock) da kendi `refetch()`'ini forwardRef ile dışa
  // verir — sayfa artık iki bağımsız veri bloğu (Hatırlatmalar + Datalar)
  // barındırıyor, her ikisinin de kendi LeadDetayModal'ı var. Hangi modalden
  // kayıt yapılırsa yapılsın HER İKİ blok da tazelenmeli (bkz. proje-yoneticisi
  // talimatı, 2026-08-13) — bağlam (filtre/sekme/sayfa/arama) korunarak, tam
  // remount YOK.
  const datalarRef = useRef(null);
  const handleLeadSaved = () => {
    remindersRef.current?.refetch();
    datalarRef.current?.refetch();
    salesReqPanelRef.current?.refetch();
    fetchSummary();
  };
  // DatalarBlock kendi LeadDetayModal'ından kaydedince bu callback ile üst
  // bileşendeki Hatırlatmalar bloğunu ve özeti tazeler (DatalarBlock kendi
  // sekmesini zaten kendi içinde tazeliyor). Satış Taleplerim paneli de aynı
  // şekilde tazelenir — DatalarBlock içindeki LeadDetayModal'dan "Satışa Hazır
  // İşaretle" ile yeni talep açılabilir.
  const handleDatalarLeadSaved = () => {
    remindersRef.current?.refetch();
    salesReqPanelRef.current?.refetch();
    fetchSummary();
  };

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(false);
    try {
      const r = await axios.get(`${API}/danisman/cockpit/summary`, { headers: authHeaders() });
      setSummary(r.data);
      if (r.data.mola?.aktif && r.data.mola?.baslangic) {
        setMolaBaslangic(new Date(r.data.mola.baslangic));
      } else {
        setMolaBaslangic(null);
      }
    } catch (err) {
      setSummaryError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSummaryLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Mola sayacı
  useEffect(() => {
    if (!molaBaslangic) { setMolaElapsed(0); return; }
    const timer = setInterval(() => {
      setMolaElapsed(Math.floor((Date.now() - molaBaslangic.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [molaBaslangic]);

  const toggleMola = async () => {
    const newAktif = !(summary?.mola?.aktif);
    setMolaLoading(true);
    try {
      await axios.post(`${API}/danisman/cockpit/mola`, { aktif: newAktif }, { headers: authHeaders() });
      toast.success(newAktif ? tr('Mola başladı', 'Break started') : tr('Mola bitti', 'Break ended'));
      fetchSummary();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setMolaLoading(false);
    }
  };

  // Faz D: `tab` — 'varsayilan' (gecikmis+bugun, param'sız) | 'yaklasan'
  // (?pencere=yaklasan). Modal içindeki sekme geçişi de bu fonksiyonu
  // yeniden çağırır (bkz. OdemeModal onTabChange).
  const fetchOdeme = async (tab = 'varsayilan') => {
    setOdemeLoading(true);
    try {
      const qs = tab === 'yaklasan' ? '&pencere=yaklasan' : '';
      const r = await axios.get(`${API}/danisman/cockpit/odemesi-gelenler?limit=100${qs}`, {
        headers: authHeaders(),
      });
      setOdemeItems(r.data.items || []);
      setOdemeTab(tab);
      setOdemeModal(true);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setOdemeLoading(false);
    }
  };

  const fmtMolaElapsed = () => {
    const h = Math.floor(molaElapsed / 3600);
    const m = Math.floor((molaElapsed % 3600) / 60);
    const s = molaElapsed % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[rootBreadcrumb(user?.role, tr), { label: tr('Danışman Paneli', 'Advisor Panel') }]}
          title={tr('Danışman Paneli', 'Advisor Panel')}
          subtitle={tr('Ödemesi gelenler, eskiyen hatırlatmalar ve datalar tek ekranda.', 'Upcoming payments, overdue reminders and leads data in one screen.')}
        />

        {/* Mola butonu — ayrı bir üst şerit, iki ana bloğun parçası değil */}
        <div className="flex justify-end mb-3">
          <motion.button
            onClick={toggleMola}
            disabled={molaLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm transition-all disabled:opacity-50 ${
              summary?.mola?.aktif ? 'bg-orange-500 text-white shadow-md' : 'bg-white hover:border-orange-300'
            }`}
            style={{
              borderRadius: 14,
              border: summary?.mola?.aktif ? '1px solid #C2410C' : '1px solid var(--crm-card-border)',
              color: summary?.mola?.aktif ? 'white' : 'var(--crm-navy-text)',
            }}
          >
            <Coffee size={18} className={summary?.mola?.aktif ? 'text-white' : 'text-orange-500'} />
            <span>
              {summary?.mola?.aktif
                ? `${tr('Molada', 'On Break')} — ${fmtMolaElapsed()}`
                : tr('MOLA / YEMEK ARASI', 'BREAK / LUNCH')}
            </span>
          </motion.button>
        </div>

        {/* Blok 1 — Ödemesi Gelenler özeti. Her ana blok kendi ErrorBoundary'si
            içinde — bir bloktaki beklenmedik render hatası diğerlerini
            (ve sayfanın geri kalanını) beyaz ekrana düşürmesin (bkz.
            proje-yoneticisi görev talimatı 2026-08-13, Kusur 2). */}
        <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
          <div className="bg-white p-5 mb-5" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <h3 className="text-xs font-bold uppercase tracking-wide mb-3 inline-block border-b-2 pb-1" style={{ color: 'var(--crm-navy)', borderColor: 'var(--crm-navy)' }}>
              {tr('ÖDEMESİ GELENLER (Ayrıntılar için TIKLA)', 'UPCOMING PAYMENTS (Click for Details)')}
            </h3>
            {summaryLoading && (
              <div className="h-9 w-64 rounded-xl bg-gray-100 animate-pulse" />
            )}
            {!summaryLoading && summaryError && <ErrorRetryCrm onRetry={fetchSummary} tr={tr} />}
            {!summaryLoading && !summaryError && (
              <div className="flex flex-wrap items-center gap-2">
                <motion.button
                  onClick={() => fetchOdeme('varsayilan')}
                  disabled={odemeLoading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border-2 border-red-200 text-red-700 font-bold text-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  <CreditCard size={16} />
                  {tr(
                    `${summary?.odemesi_gelenler?.adet ?? 0} adet içinde ödenmeyen ${fmtMoney(summary?.odemesi_gelenler?.odenmeyen_toplam ?? 0)}`,
                    `${summary?.odemesi_gelenler?.adet ?? 0} items, ${fmtMoney(summary?.odemesi_gelenler?.odenmeyen_toplam ?? 0)} unpaid`
                  )}
                </motion.button>
                {/* Faz D: yaklaşan (önümüzdeki 7 gün) ödemeler — ayrı, additive
                    sayaç (bkz. cockpit_summary yaklasan_odemeler). Badge'in
                    (gecikmiş+bugün) anlamı DEĞİŞMEDİ, bu ikinci bir buton. */}
                <motion.button
                  onClick={() => fetchOdeme('yaklasan')}
                  disabled={odemeLoading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-50 border-2 border-cyan-200 text-cyan-700 font-bold text-sm hover:bg-cyan-100 transition-colors disabled:opacity-50"
                >
                  <CalendarClock size={16} />
                  {tr(
                    `Yaklaşan (7 gün): ${summary?.yaklasan_odemeler?.adet ?? 0} adet, ${fmtMoney(summary?.yaklasan_odemeler?.toplam ?? 0)}`,
                    `Upcoming (7 days): ${summary?.yaklasan_odemeler?.adet ?? 0} items, ${fmtMoney(summary?.yaklasan_odemeler?.toplam ?? 0)}`
                  )}
                </motion.button>
              </div>
            )}
          </div>
        </ErrorBoundary>

        {/* Satış Taleplerim (danışman) — Ödemesi Gelenler'in ALTI, Hatırlatmalar'ın
            ÜSTÜ, varsayılan KAPALI (bkz. proje-yoneticisi görev talimatı,
            2026-08-13 — Atanan Leadler kaldırma turu). Admin/superadmin
            görmez (bkz. SatisTalepleriPanel dosya başı yorumu). */}
        {!isAdmin && (
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <SatisTalepleriPanel
              ref={salesReqPanelRef}
              id="satis-taleplerim-panel"
              lang={lang}
              authHeaders={authHeaders}
              defaultOpen={salesReqDeepLink}
            />
          </ErrorBoundary>
        )}

        {/* Blok 2 — Hatırlatmalar (Eskiyen 50) */}
        <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
          <div className="bg-white overflow-hidden" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <div className="flex items-center gap-2 px-5 py-3 border-b-2" style={{ borderColor: 'var(--crm-card-border)' }}>
              <AlarmClock size={16} style={{ color: 'var(--crm-navy)' }} />
              <h3 className="font-bold text-sm" style={{ color: 'var(--crm-navy-text)' }}>
                {tr('Hatırlatmalar (Eskiyen 50)', 'Reminders (Overdue 50)')}
              </h3>
            </div>
            <HatirlatmalarPanel
              ref={remindersRef}
              scope="eskiyen"
              variant="table"
              limit={50}
              lang={lang}
              authHeaders={authHeaders}
              isAdmin={isAdmin}
              onLeadClick={openLeadDetay}
              emptyText={tr('Eskiyen hatırlatma yok', 'No overdue reminders')}
            />
          </div>
        </ErrorBoundary>

        {/* Blok 3 — Datalar (eskiden /danisman/datalar sayfası) */}
        <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
          <DatalarBlock
            ref={datalarRef}
            lang={lang}
            tr={tr}
            user={user}
            authHeaders={authHeaders}
            onLeadSaved={handleDatalarLeadSaved}
          />
        </ErrorBoundary>

        {/* Lead detay modalı — ayrı blok, kendi ErrorBoundary'si (bir hata
            diğer bloklara ya da açık modal dışındaki sayfaya sıçramasın) */}
        <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
          <LeadDetayModal
            lead={gorusmeLead}
            lang={lang}
            statusOptions={statusOptions}
            authHeaders={authHeaders}
            isAdmin={isAdmin}
            currentUserId={user?.user_id}
            onClose={() => setGorusmeLead(null)}
            onSaved={handleLeadSaved}
          />
        </ErrorBoundary>

        {/* Ödeme modalı — Faz D: sekme (varsayilan/yaklasan) modal İÇİNDE
            değiştirilebilir, her sekme kendi adet/toplam'ını (summary'den,
            liste ile TUTARLI) ve satır bazlı pencere_durumu rozetini gösterir. */}
        {odemeModal && (
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <OdemeModal
              items={odemeItems}
              adet={
                odemeTab === 'yaklasan'
                  ? (summary?.yaklasan_odemeler?.adet ?? 0)
                  : (summary?.odemesi_gelenler?.adet ?? 0)
              }
              toplam={
                odemeTab === 'yaklasan'
                  ? (summary?.yaklasan_odemeler?.toplam ?? 0)
                  : (summary?.odemesi_gelenler?.odenmeyen_toplam ?? 0)
              }
              activeTab={odemeTab}
              onTabChange={fetchOdeme}
              tabLoading={odemeLoading}
              lang={lang}
              onClose={() => setOdemeModal(false)}
            />
          </ErrorBoundary>
        )}
      </div>
    </AdminLayout>
  );
}
